import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import styled from '@emotion/native';
import {
  SectionHeading,
  StyledAsterisksText,
  OuterImageContainer,
  ImageContainer,
  ProfileImage,
  EditButton,
  SubSection,
  InputRow,
  FormSection,
  DescriptionText,
  InputColumnSection,
} from '@/shared/styles/styledComponents/editProfileForm';
import { lightGreen, mint } from '@/shared/styles/colors';
import { BaseText } from '@/shared/components/StyledText';
import { ProfileFormDataWithoutCheckBox } from '@/shared/types/stylist';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import ErrorFrame from '@/shared/components/ErrorFrame';
import { NAME_MAX_LENGTH } from '@/shared/constants/utility';
import StaticInput from '@/shared/components/StaticInput';
import useStore from '@/stores/useStore';
import { GENDER_OPTIONS, getGenderLabel } from '@/shared/constants/onboarding';
import { Gender } from '@/shared/types/generated';
import Dropdown from '@/shared/components/Dropdown';
import DateInput from '@/shared/components/DateInput';
import AddIcon from '@/shared/components/AddIcon';
import EditIcon from '@/shared/components/EditIcon';

interface BasicInfoSectionProps {
  profileFormData: ProfileFormDataWithoutCheckBox;
  setProfileFormData: React.Dispatch<
    React.SetStateAction<ProfileFormDataWithoutCheckBox>
  >;
  selectImage: () => Promise<void>;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  profileFormData,
  setProfileFormData,
  selectImage,
}) => {
  const { isDesktop } = useBreakpoint();
  const isStylist = useStore((state) => state.isStylist);

  const [isGenderMenuVisible, setIsGenderMenuVisible] = useState(false);

  const subSectionMarginBottom = isDesktop ? 12 : 18;

  return (
    <FormSection
      isDesktop={isDesktop}
      marginBottom={isStylist ? undefined : 12}
    >
      <TitleRow marginBottom={!isStylist ? (isDesktop ? 32 : 20) : 0}>
        <SectionHeading
          isDesktop={isDesktop}
          style={!isStylist ? { marginBottom: 0 } : undefined}
        >
          {isStylist ? 'Basic Info' : 'Profile Info'}
        </SectionHeading>
        {!isStylist && <RequiredIndicator>*Required</RequiredIndicator>}
      </TitleRow>
      {!isStylist && (
        <DescriptionText marginBottom={isDesktop ? 18 : 24}>
          This information is shown to stylists in your profile.
        </DescriptionText>
      )}
      <StyledAsterisksText>Profile Image*:</StyledAsterisksText>
      <OuterImageContainer isDesktop={isDesktop}>
        <TouchableOpacity onPress={selectImage}>
          <ImageContainer isDesktop={isDesktop}>
            {profileFormData.profileImage ? (
              <>
                <ProfileImage source={{ uri: profileFormData.profileImage }} />
                <EditButton onPress={selectImage} bottom={12}>
                  <EditIcon size={isDesktop ? 40 : 48} />
                </EditButton>
              </>
            ) : (
              <AddIcon />
            )}
          </ImageContainer>
        </TouchableOpacity>
      </OuterImageContainer>

      <StyledAsterisksText marginBottom={24}>Full Name*</StyledAsterisksText>
      <SubSection marginBottom={subSectionMarginBottom}>
        <InputRow isDesktop={isDesktop}>
          <ErrorFrame
            errorText={`Cannot exceed ${NAME_MAX_LENGTH} characters`}
            errorVisible={profileFormData.firstName.length > NAME_MAX_LENGTH}
            containerStyle={{ flex: 1 }}
          >
            <StaticInput
              value={profileFormData.firstName ?? ''}
              onChange={(e) =>
                setProfileFormData({
                  ...profileFormData,
                  firstName: e.nativeEvent.text,
                })
              }
              placeholderText="Enter first name here"
              labelText="First Name"
            />
          </ErrorFrame>
          <ErrorFrame
            errorText={`Cannot exceed ${NAME_MAX_LENGTH} characters`}
            errorVisible={profileFormData.lastName.length > NAME_MAX_LENGTH}
            containerStyle={{ flex: 1 }}
          >
            <StaticInput
              value={profileFormData.lastName ?? ''}
              onChange={(e) =>
                setProfileFormData({
                  ...profileFormData,
                  lastName: e.nativeEvent.text,
                })
              }
              placeholderText="Enter last name here"
              labelText="Last Name"
            />
          </ErrorFrame>
        </InputRow>
      </SubSection>

      {isStylist && (
        <>
          <InputRow isDesktop={isDesktop}>
            <InputColumnSection isDesktop={isDesktop}>
              <>
                <StyledAsterisksText>Gender*</StyledAsterisksText>
                <DescriptionText>
                  This helps clients filter and match with stylists during
                  instant styling. Selecting “Prefer not to say” means only
                  clients who choose “No Preference” will see you.
                </DescriptionText>
              </>
              <Dropdown
                eyebrowText="Gender"
                options={GENDER_OPTIONS}
                selectedOptionLabel={getGenderLabel(profileFormData.gender)}
                selectedOption={profileFormData.gender || 'Select an option'}
                onSelect={(option) =>
                  setProfileFormData({
                    ...profileFormData,
                    gender: option.value as Gender,
                  })
                }
                isVisible={isGenderMenuVisible}
                setIsVisible={setIsGenderMenuVisible}
                customDropdownButtonStyle={{ width: '100%' }}
                dropdownTextStyle={{ fontSize: 16 }}
                selectedOptionContentStyle={{ backgroundColor: mint }}
                selectedOptionTextStyle={{ fontWeight: 'bold' }}
              />
            </InputColumnSection>
            <InputColumnSection isDesktop={isDesktop}>
              <>
                <StyledAsterisksText>Birthday*</StyledAsterisksText>
                <DescriptionText>
                  This helps clients find stylists in their preferred age range.
                  While your birthdate isn&apos;t shown publicly, it may be used
                  to match you with clients.
                </DescriptionText>
              </>
              <DateInput
                labelText="Birthday"
                // Same bottom margin as the Gender dropdown, so the two
                // columns are the same height and their fields line up
                containerStyle={{ marginBottom: 28 }}
                value={profileFormData.birthDate}
                onChange={(date) =>
                  setProfileFormData({
                    ...profileFormData,
                    birthDate: date,
                  })
                }
              />
            </InputColumnSection>
          </InputRow>
        </>
      )}
    </FormSection>
  );
};

const TitleRow = styled.View<{ marginBottom?: number }>(
  ({ marginBottom = 0 }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom,
  }),
);

const RequiredIndicator = styled(BaseText)({
  fontSize: 15,
  fontWeight: '400',
  color: lightGreen,
});

export default BasicInfoSection;
